// @flow

import * as React from 'react';
import {isEqual} from 'lodash';

import {tree} from '../../generated_tree';
import {parse, normalizeCommand} from '../cli/general';
import {giveSuggestion} from '../cli/suggestions';
import {MAX_PANE_COUNT} from '../constants';
import type {Param, Command} from '../commands/resource';

type CluiDisplaySize = 'minimized' | 'normal';
type CluiOpenState = 'closed' | 'open';

export type SuggestionsType =
  | Array<[string, Command]>
  | Array<[string, Param]>
  | Array<[string, {}]>;

export type PaneState = {
  command: string,
  cursorIndex: number,
  history: Array<{
    command: string,
    output: React.Node,
    isPrintOutput?: boolean,
  }>,
  arrowHistory: Array<{command: string, output: React.Node}>,
  alteredHistory: Array<{command: string, output: React.Node}>,
  alteredIndex: number,
  runCommand: boolean,
  suggestions: SuggestionsType,
  suggestionIndex: number,
  allowInput: boolean,
  displayOn: 'input' | 'suggestion',
  showWelcomePrompt: boolean,
};

export type State = {
  size: CluiDisplaySize,
  openState: CluiOpenState,
  panes: Array<PaneState>,
  focusedPane: number,
  isFocused: boolean,
  locked: boolean,
};

export const createInitialPaneState = (): PaneState => ({
  command: '',
  cursorIndex: 0,
  history: [],
  arrowHistory: [],
  alteredHistory: [{command: '', output: ''}],
  alteredIndex: 0,
  runCommand: false,
  suggestions: giveSuggestion(parse(''), tree),
  suggestionIndex: -1,
  allowInput: true,
  displayOn: 'input',
  showWelcomePrompt: false,
});

export const createInitialState = (): State => ({
  size: 'normal',
  openState: 'closed',
  panes: [{...createInitialPaneState(), showWelcomePrompt: true}],
  focusedPane: 0,
  isFocused: false,
  locked: false,
});

export const initialState = createInitialState();

export type FocusAction = {|type: 'SET_FOCUS', payload: boolean|};
export type AddPaneAction = {|type: 'ADD_PANE'|};
export type RemovePaneAction = {|type: 'REMOVE_PANE', payload: number|};
export type FocusPaneAction = {|type: 'FOCUS_PANE', payload: number|};
export type OpenAction = {|type: 'OPEN'|};
export type CloseAction = {|type: 'CLOSE'|};
export type ToggleMinimizeAction = {|type: 'TOGGLE_MINIMIZE'|};
export type InvokeCommandAction = {|
  type: 'INVOKE_COMMAND',
  payload: {command?: string, paneIdx?: number},
|};
export type UpdatePaneAction = {|
  type: 'UPDATE_PANE',
  payload: {pane: $Shape<PaneState>, paneIdx?: number},
|};
export type ResetAction = {|type: 'RESET'|};
export type LockPanesAction = {|type: 'LOCK_PANES'|};
export type UnlockPanesAction = {|type: 'UNLOCK_PANES'|};
export type PrintOutputAction = {|
  type: 'PRINT_OUTPUT',
  payload: {paneIdx?: number, output: React.Node},
|};

export type Action =
  | FocusAction
  | AddPaneAction
  | RemovePaneAction
  | FocusPaneAction
  | OpenAction
  | CloseAction
  | ToggleMinimizeAction
  | InvokeCommandAction
  | UpdatePaneAction
  | ResetAction
  | LockPanesAction
  | UnlockPanesAction
  | PrintOutputAction;

export const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SET_FOCUS':
      return {...state, isFocused: action.payload};
    case 'ADD_PANE':
      return state.panes.length < MAX_PANE_COUNT
        ? {
            ...state,
            panes: [...state.panes, createInitialPaneState()],
            focusedPane: state.panes.length,
          }
        : state;
    case 'REMOVE_PANE':
      return {
        ...state,
        panes: [
          ...state.panes.slice(0, action.payload),
          ...state.panes.slice(action.payload + 1),
        ],
        focusedPane: Math.max(0, state.focusedPane - 1),
      };
    case 'FOCUS_PANE':
      return {
        ...state,
        isFocused: true,
        focusedPane: action.payload,
      };
    case 'CLOSE':
      return {...state, isFocused: false, openState: 'closed'};
    case 'OPEN':
      return {...state, isFocused: true, openState: 'open', size: 'normal'};
    case 'TOGGLE_MINIMIZE': {
      const newSize = state.size === 'minimized' ? 'normal' : 'minimized';
      return {
        ...state,
        isFocused: newSize !== 'minimized',
        size: newSize,
      };
    }
    case 'INVOKE_COMMAND': {
      let paneIdx =
        action?.payload?.paneIdx ??
        state.panes.findIndex(({allowInput}) => allowInput);
      paneIdx = paneIdx >= 0 ? paneIdx : state.panes.length;

      const needNewPane = paneIdx === state.panes.length;

      const panes = needNewPane
        ? [
            ...state.panes,
            {
              ...createInitialPaneState(),
              command: normalizeCommand(action.payload?.command ?? ''),
              runCommand: true,
              allowInput: false,
            },
          ]
        : [
            ...state.panes.slice(0, paneIdx),
            {
              ...state.panes[paneIdx],
              command: normalizeCommand(
                action.payload?.command ?? state.panes[paneIdx].command,
              ),
              runCommand: true,
              allowInput: false,
            },
            ...state.panes.slice(paneIdx + 1),
          ];

      return {
        ...state,
        isFocused: true,
        focusedPane: paneIdx,
        size: 'normal',
        openState: 'open',
        panes,
      };
    }
    case 'UPDATE_PANE': {
      const paneIdx = action?.payload?.paneIdx ?? state.focusedPane;
      const newPane = {
        ...state.panes[paneIdx],
        ...action.payload.pane,
      };

      if (newPane.cursorIndex === newPane.command.length) {
        const newSuggestions = giveSuggestion(parse(newPane.command), tree);
        newPane.suggestions = newSuggestions;
      } else {
        // Seems silly but Flow needs this inline annotation to avoid [speculation-ambiguous]
        const emptySuggestions: Array<[string, Command]> = [];
        newPane.suggestions = emptySuggestions;
      }

      if (!action.payload.pane.hasOwnProperty('suggestionIndex')) {
        newPane.suggestionIndex = !isEqual(
          newPane.suggestions,
          state.panes[paneIdx].suggestions,
        )
          ? -1
          : state.panes[paneIdx].suggestionIndex;
      }

      return {
        ...state,
        panes: [
          ...state.panes.slice(0, paneIdx),
          newPane,
          ...state.panes.slice(paneIdx + 1),
        ],
      };
    }
    case 'RESET': {
      return {
        ...createInitialState(),
        isFocused: state.isFocused,
        size: state.size,
      };
    }
    case 'LOCK_PANES': {
      return {
        ...state,
        locked: true,
      };
    }
    case 'UNLOCK_PANES': {
      return {
        ...state,
        locked: false,
      };
    }
    case 'PRINT_OUTPUT': {
      const paneIdx = action.payload?.paneIdx ?? state.focusedPane;

      const newPane = {
        ...state.panes[paneIdx],
        history: [
          ...state.panes[paneIdx].history,
          {command: '', output: action.payload.output, isPrintOutput: true},
        ],
      };

      return {
        ...state,
        panes: [
          ...state.panes.slice(0, paneIdx),
          newPane,
          ...state.panes.slice(paneIdx + 1),
        ],
      };
    }
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};
